import React from 'react';
import { Card } from '@repo/ui/Card';

import { gql } from '@apollo/client';

import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';
import { Form, TextInputField, SelectorInputField } from '@repo/ui/Form';

import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { Country } from 'country-state-city';

import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';
import { useAccount } from '@/hooks/useAccount';

import type { Mercury } from '@repo/types/Models';

const companyDetailsFormSchema = z.object({
  address1: z.string().min(1, { message: 'Please include your primary address line.' }),
  address2: z.string().optional().or(z.literal('')).nullable(),
  city: z.string().min(1, { message: 'Please include a city/town.' }),
  stateCode: z.string().min(1, { message: 'Please include a state/province.' }),
  zipCode: z.string().min(1, { message: 'Please include a zip code.' }),
  countryCode: z.string().min(1, { message: 'Please include a country.' }),
});

type CompanyDetailsFormParams = TypeOf<typeof companyDetailsFormSchema>;

interface CompanyDetailsFormProps {
  accountAddress?: Mercury.Models.AccountAddress | null;
  refetch: () => void;
}

const CompanyDetailsForm = ({ accountAddress, refetch }: CompanyDetailsFormProps) => {
  const user = useUser();
  const currentAccount = useAccount();

  const isCreateMode = !accountAddress;

  const userCanCreate = POLICIES['account_addresses:create'](user);
  const userCanUpdate = POLICIES['account_addresses:update'](user);
  const userCanModify = isCreateMode ? userCanCreate : userCanUpdate;

  const UPDATE_ACCOUNT_ADDRESS = gql`
    mutation UpdateAccountAddress($id: ID!, $input: UpdateAccountAddressInput!) {
      updateAccountAddress(id: $id, input: $input) {
        accountAddress {
          id
        }
      }
    }
  `;

  const CREATE_ADDRESS_FOR_ACCOUNT = gql`
    mutation CreateAddressForAccount($accountId: ID!, $input: CreateAddressForAccountInput!) {
      createAddressForAccount(accountId: $accountId, input: $input) {
        accountAddress {
          id
        }
      }
    }
  `;

  const client = useApolloClient();

  const onSubmit = async (input: CompanyDetailsFormParams) => {
    if (isCreateMode) {
      await client.mutate({
        mutation: CREATE_ADDRESS_FOR_ACCOUNT,
        variables: {
          input,
          accountId: currentAccount?.id,
        },
      });
    } else {
      await client.mutate({
        mutation: UPDATE_ACCOUNT_ADDRESS,
        variables: {
          input,
          id: accountAddress?.id,
        },
      });
    }
    refetch();
  };

  const form = useZodForm({
    schema: companyDetailsFormSchema,
    onSubmit,
    defaultValues: {
      address1: accountAddress?.address1 || '',
      address2: accountAddress?.address2 || '',
      city: accountAddress?.city || '',
      stateCode: accountAddress?.stateCode || '',
      zipCode: accountAddress?.zipCode || '',
      countryCode: accountAddress?.countryCode || '',
    },
  });

  const countries = Country.getAllCountries().map((country) => ({
    label: country.name,
    value: country.isoCode,
  }));

  return (
    <Card title="Company details">
      <Form {...form} className="max-w-md">
        <Form.Success>
          Company details successfully {isCreateMode ? 'created' : 'updated'}.
        </Form.Success>
        <Form.Error>
          Error while trying to {isCreateMode ? 'create' : 'update'} company details. Please try
          again later or contact support if error persists.
        </Form.Error>

        <TextInputField label="Business name" disabled value={currentAccount?.name || ''} />
        <TextInputField
          label="Address line 1"
          disabled={!userCanModify}
          {...registerInput(form, 'address1')}
        />
        <TextInputField
          label="Address line 2"
          disabled={!userCanModify}
          {...registerInput(form, 'address2')}
        />
        <TextInputField
          label="City"
          width="half"
          disabled={!userCanModify}
          {...registerInput(form, 'city')}
        />
        <TextInputField
          label="State/Province"
          width="half"
          disabled={!userCanModify}
          {...registerInput(form, 'stateCode')}
        />
        <SelectorInputField
          label="Country"
          width="half"
          disabled={!userCanModify}
          options={countries}
          {...registerInput(form, 'countryCode')}
        />
        <TextInputField
          label="Zip/Postal code"
          width="half"
          disabled={!userCanModify}
          {...registerInput(form, 'zipCode')}
        />

        <div className="w-full">
          <Button type="submit" disabled={!userCanModify}>
            Save details
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default CompanyDetailsForm;
